/**
 * 兑换码管理页面
 * 管理员可以创建、查看、禁用/启用兑换码
 */

import { LayoutWithSidebar } from "@/components/LayoutWithSidebar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type CreateRedemptionCodeRequest,
  type RedemptionCode,
  createRedemptionCodes,
  deleteRedemptionCode,
  formatDate,
  formatQuota,
  getRedemptionCodeList,
  getRedemptionCodeStats,
  getRedemptionCodeStatusInfo,
  updateRedemptionCodeStatus,
} from "@/services/redemptionCodeApi";
import { CheckCircle, Plus, Power, Search, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export function RedemptionCodeManagementPage() {
  const [codes, setCodes] = useState<RedemptionCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [keyword, setKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [selectedCode, setSelectedCode] = useState<RedemptionCode | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<"active" | "disabled">("active");
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    used: 0,
    disabled: 0,
  });

  const [newCodeData, setNewCodeData] = useState<CreateRedemptionCodeRequest>({
    name: "",
    quota: 100,
    count: 1,
  });

  // 加载兑换码列表
  const loadCodes = async () => {
    try {
      setLoading(true);
      const response = await getRedemptionCodeList({
        page,
        pageSize,
        keyword: keyword || undefined,
        status: statusFilter || undefined,
      });

      if (response.success) {
        setCodes(response.data.codes);
        setTotal(response.data.pagination.total);
      } else {
        toast.error(response.message || "获取兑换码列表失败");
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "获取兑换码列表失败"
      );
    } finally {
      setLoading(false);
    }
  };

  // 加载统计信息
  const loadStats = async () => {
    try {
      const response = await getRedemptionCodeStats();
      if (response.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error("获取统计信息失败:", error);
    }
  };

  useEffect(() => {
    loadCodes();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter]);

  // 搜索处理
  const handleSearch = () => {
    setPage(1);
    loadCodes();
  };

  // 按回车搜索
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // 创建兑换码
  const handleCreateCodes = async () => {
    if (!newCodeData.name.trim()) {
      toast.error("请输入兑换码名称");
      return;
    }
    if (newCodeData.quota <= 0) {
      toast.error("配额必须大于0");
      return;
    }
    if (newCodeData.count <= 0 || newCodeData.count > 100) {
      toast.error("生成数量必须在1-100之间");
      return;
    }

    try {
      setLoading(true);
      const response = await createRedemptionCodes(newCodeData);

      if (response.success) {
        toast.success(`成功创建 ${newCodeData.count} 个兑换码`);
        setCreateDialogOpen(false);
        setNewCodeData({ name: "", quota: 100, count: 1 });
        loadCodes();
        loadStats();
      } else {
        toast.error(response.message || "创建兑换码失败");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "创建兑换码失败");
    } finally {
      setLoading(false);
    }
  };

  // 更新兑换码状态
  const handleUpdateStatus = async () => {
    if (!selectedCode) return;

    try {
      setLoading(true);
      const response = await updateRedemptionCodeStatus(
        selectedCode.id,
        newStatus
      );

      if (response.success) {
        toast.success("更新状态成功");
        setStatusDialogOpen(false);
        loadCodes();
        loadStats();
      } else {
        toast.error(response.message || "更新状态失败");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "更新状态失败");
    } finally {
      setLoading(false);
    }
  };

  // 删除兑换码
  const handleDelete = async () => {
    if (!selectedCode) return;

    try {
      setLoading(true);
      const response = await deleteRedemptionCode(selectedCode.id);

      if (response.success) {
        toast.success("删除成功");
        setDeleteDialogOpen(false);
        loadCodes();
        loadStats();
      } else {
        toast.error(response.message || "删除失败");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "删除失败");
    } finally {
      setLoading(false);
    }
  };

  // 复制兑换码
  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      toast.success("兑换码已复制到剪贴板");
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      toast.error("复制失败");
    }
  };

  // 打开状态对话框
  const openStatusDialog = (code: RedemptionCode) => {
    setSelectedCode(code);
    setNewStatus(code.status === "active" ? "disabled" : "active");
    setStatusDialogOpen(true);
  };

  // 打开删除对话框
  const openDeleteDialog = (code: RedemptionCode) => {
    setSelectedCode(code);
    setDeleteDialogOpen(true);
  };

  const statusInfo = (status: string | undefined) =>
    getRedemptionCodeStatusInfo(status);

  return (
    <LayoutWithSidebar>
      <div className="container mx-auto p-6 space-y-6">
        {/* 标题和操作栏 */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">兑换码管理</h1>
            <p className="text-muted-foreground mt-1">创建和管理兑换码</p>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            批量创建兑换码
          </Button>
        </div>

        {/* 统计卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                总数
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                可用
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {stats.active}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                已使用
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-600">
                {stats.used}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                已禁用
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {stats.disabled}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* 筛选和搜索 */}
        <Card>
          <CardHeader>
            <CardTitle>兑换码列表</CardTitle>
            <CardDescription>查看和管理所有兑换码</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 mb-4">
              <div className="flex-1 flex gap-2">
                <Input
                  placeholder="搜索兑换码名称或代码..."
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="max-w-sm"
                />
                <Button onClick={handleSearch} variant="outline">
                  <Search className="mr-2 h-4 w-4" />
                  搜索
                </Button>
              </div>
              <Select value={statusFilter || "all"} onValueChange={(value) => setStatusFilter(value === "all" ? "" : value)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="筛选状态" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部状态</SelectItem>
                  <SelectItem value="active">可用</SelectItem>
                  <SelectItem value="used">已使用</SelectItem>
                  <SelectItem value="disabled">已禁用</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* 兑换码表格 */}
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>兑换码</TableHead>
                    <TableHead>名称</TableHead>
                    <TableHead>配额</TableHead>
                    <TableHead>状态</TableHead>
                    <TableHead>创建者</TableHead>
                    <TableHead>使用者</TableHead>
                    <TableHead>创建时间</TableHead>
                    <TableHead>使用时间</TableHead>
                    <TableHead className="text-right">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center">
                        加载中...
                      </TableCell>
                    </TableRow>
                  ) : codes.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        className="text-center text-muted-foreground"
                      >
                        暂无数据
                      </TableCell>
                    </TableRow>
                  ) : (
                    codes.map((code) => (
                      <TableRow key={code.id}>
                        <TableCell className="font-mono text-sm">
                          <div className="flex items-center gap-2">
                            <span
                              className="cursor-pointer hover:text-primary"
                              onClick={() => handleCopyCode(code.code)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  handleCopyCode(code.code);
                                }
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              {code.code}
                            </span>
                            {copiedCode === code.code && (
                              <CheckCircle className="h-3 w-3 text-green-600" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{code.name}</TableCell>
                        <TableCell>{formatQuota(code.quota)}</TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              code.status === "active"
                                ? "default"
                                : code.status === "used"
                                  ? "secondary"
                                  : "destructive"
                            }
                          >
                            {statusInfo(code.status).name}
                          </Badge>
                        </TableCell>
                        <TableCell>{code.createdBy}</TableCell>
                        <TableCell>{code.redeemedBy || "-"}</TableCell>
                        <TableCell>{formatDate(code.createdAt)}</TableCell>
                        <TableCell>{formatDate(code.redeemedAt)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {code.status !== "used" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openStatusDialog(code)}
                              >
                                <Power className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteDialog(code)}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* 分页 */}
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                共 {total} 条记录
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1 || loading}
                >
                  上一页
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page * pageSize >= total || loading}
                >
                  下一页
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 创建兑换码对话框 */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>批量创建兑换码</DialogTitle>
            <DialogDescription>创建指定数量和配额的兑换码</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">兑换码名称</Label>
              <Input
                id="name"
                placeholder="例如：新春活动充值码"
                value={newCodeData.name}
                onChange={(e) =>
                  setNewCodeData({ ...newCodeData, name: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="quota">配额数量</Label>
              <Input
                id="quota"
                type="number"
                min="1"
                value={newCodeData.quota}
                onChange={(e) =>
                  setNewCodeData({
                    ...newCodeData,
                    quota: Number.parseInt(e.target.value) || 0,
                  })
                }
              />
              <p className="text-sm text-muted-foreground">
                每个兑换码包含的配额数量
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="count">生成数量</Label>
              <Input
                id="count"
                type="number"
                min="1"
                max="100"
                value={newCodeData.count}
                onChange={(e) =>
                  setNewCodeData({
                    ...newCodeData,
                    count: Number.parseInt(e.target.value) || 1,
                  })
                }
              />
              <p className="text-sm text-muted-foreground">
                一次最多生成 100 个兑换码
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
            >
              取消
            </Button>
            <Button onClick={handleCreateCodes} disabled={loading}>
              {loading ? "创建中..." : "创建"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 更新状态对话框 */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>更新兑换码状态</DialogTitle>
            <DialogDescription>
              确定要{newStatus === "active" ? "启用" : "禁用"}兑换码吗？
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setStatusDialogOpen(false)}
            >
              取消
            </Button>
            <Button onClick={handleUpdateStatus} disabled={loading}>
              {loading ? "更新中..." : "确认"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 删除确认对话框 */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除兑换码{" "}
              <span className="font-mono font-bold">{selectedCode?.code}</span>{" "}
              吗？ 此操作无法撤销。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={loading}>
              {loading ? "删除中..." : "确认删除"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </LayoutWithSidebar>
  );
}
